import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { classNames } from "@/utils/classNames";

export type LeadContextView = "lead-panel" | "chat";

/**
 * Top-level Lead Panel | Chat toggle that sits above the lead-context views
 * (v81 mockup `lead-ctx-tabs`). "Lead Panel" shows the full panel with its own
 * Overview/Research/Timeline sub-tabs; "Chat" shows the per-lead outreach view.
 * Rendered only when a Chat view is actually available for the lead.
 */
export const LeadContextTabs: FunctionComponent<{
	view: LeadContextView;
	onChange: (view: LeadContextView) => void;
	/** Optional draft/sequence count shown as a badge on the Chat tab. */
	chatBadgeCount?: number;
}> = ({ view, onChange, chatBadgeCount }) => {
	const { t } = useTranslation();

	const renderTab = (value: LeadContextView, label: string, badge?: number) => (
		<button
			className={classNames(
				"flex cursor-pointer items-center justify-center gap-1 border-b-2 py-2.5 text-center text-xs font-semibold transition-colors",
				view === value
					? "border-blue-action text-blue-action"
					: "border-transparent text-text-meta hover:bg-surface-hover hover:text-text"
			)}
			type="button"
			onClick={() => onChange(value)}
		>
			{label}
			{badge !== undefined && badge > 0 && (
				<span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-blue-action px-1.5 text-[9.5px] font-bold text-white">
					{badge}
				</span>
			)}
		</button>
	);

	return (
		<div
			className="grid shrink-0 grid-cols-2 border-b border-border-light bg-white"
			data-testid="lead-context-tabs"
		>
			{renderTab("lead-panel", t("contacts.leadPanel.contextTabs.leadPanel"))}
			{renderTab("chat", t("contacts.leadPanel.contextTabs.chat"), chatBadgeCount)}
		</div>
	);
};
